import { useState, useRef, useEffect } from "react";
import { X, CaretDown, Confetti, WhatsappLogo } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#042E45";
const BLUE = "#025582";
const PINK = "#E60059";
const GRAY = "#4A4A4A";

const COUNTRIES = [
  ["ar", "Argentina"], ["bz", "Belize"], ["bo", "Bolivia"], ["br", "Brasil"],
  ["cl", "Chile"], ["co", "Colombia"], ["cr", "Costa Rica"], ["cu", "Cuba"],
  ["ec", "Ecuador"], ["sv", "El Salvador"], ["gt", "Guatemala"], ["hn", "Honduras"],
  ["mx", "México"], ["ni", "Nicaragua"], ["pa", "Panamá"], ["py", "Paraguay"],
  ["pe", "Perú"], ["do", "República Dominicana"], ["uy", "Uruguay"],
  ["ve", "Venezuela"], ["us", "Estados Unidos"], ["es", "España"],
];

const Flag = ({ code, size = 22 }) => (
  <img
    src={`https://flagcdn.com/w40/${code}.png`}
    srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
    alt=""
    loading="lazy"
    className="rounded-[2px] object-cover shrink-0"
    style={{ width: size, height: Math.round(size * 0.72) }}
  />
);

export default function CountryInterestModal({ open, onClose, initialCountry, waLink }) {
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState("");
  const [ddOpen, setDdOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    if (open) { setStep(1); setCountry(initialCountry || ""); setDdOpen(false); }
  }, [open, initialCountry]);

  useEffect(() => {
    const onClick = (e) => { if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!open) return null;

  const selected = COUNTRIES.find(([c]) => c === country);

  const submit = async () => {
    setSending(true);
    try {
      await fetch(`${BACKEND}/api/web/country-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country: selected ? selected[1] : country }),
      });
    } catch { /* noop */ }
    setSending(false);
    setStep(2);
  };

  const continuar = () => {
    if (waLink) window.open(waLink, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(24,52,75,.55)", backdropFilter: "blur(2px)" }}
      data-testid="country-interest-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-lg rounded-3xl bg-white p-8 md:p-10" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
        <button onClick={onClose} data-testid="modal-close" className="absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#f1f1f1", color: NAVY }}>
          <X size={18} weight="bold" />
        </button>

        {step === 1 && (
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold leading-tight mb-3 pr-8" style={{ color: NAVY }}>¿Querés que Héroes Migrantes llegue a tu país?</h3>
            <p className="text-base mb-6" style={{ color: GRAY }}>Hoy estamos en Guatemala. Al confirmar, le pedís a Héroes Migrantes que llegue a tu país — todavía no operamos ahí, pero así nos ayudás a medir dónde hay interés.</p>
            <label className="block text-sm font-bold mb-2" style={{ color: NAVY }}>Elegí tu país:</label>
            <div className="relative" ref={ddRef}>
              <button
                onClick={() => setDdOpen((o) => !o)}
                data-testid="country-dd-trigger"
                className="w-full flex items-center justify-between gap-2 rounded-xl px-4 py-3.5 text-left"
                style={{ border: "1.5px solid #d9d3cc", color: selected ? NAVY : "#9ca3af" }}
              >
                <span className="flex items-center gap-2.5 font-bold">
                  {selected && <Flag code={selected[0]} />}
                  {selected ? selected[1] : "Selecciona una opción"}
                </span>
                <CaretDown size={16} weight="bold" style={{ color: NAVY, transform: ddOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
              </button>
              {ddOpen && (
                <div data-testid="country-dd-list" className="absolute left-0 right-0 mt-2 max-h-64 overflow-y-auto rounded-xl bg-white p-1.5 z-10" style={{ boxShadow: "0 14px 44px rgba(24,52,75,.2)", border: "1px solid #eee" }}>
                  {COUNTRIES.map(([code, name]) => (
                    <button
                      key={code}
                      data-testid={`country-opt-${code}`}
                      onClick={() => { setCountry(code); setDdOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm font-bold hover:bg-gray-50"
                      style={{ color: NAVY }}
                    >
                      <Flag code={code} /> {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-4 mt-8">
              <button onClick={onClose} data-testid="modal-cerrar" className="text-sm font-extrabold" style={{ color: NAVY }}>Cerrar</button>
              <button
                onClick={() => country && submit()}
                disabled={!country || sending}
                data-testid="modal-confirmar"
                className="rounded-full px-7 py-3 text-sm font-extrabold text-white disabled:opacity-40"
                style={{ background: BLUE }}
              >
                {sending ? "Enviando…" : "Confirmar interés"}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="text-center py-4">
            <div className="flex justify-center mb-4"><Confetti size={44} weight="fill" style={{ color: PINK }} /></div>
            <h3 className="text-2xl font-extrabold mb-2" style={{ color: NAVY }}>¡Gracias!</h3>
            <p className="text-base mb-7" style={{ color: GRAY }}>Registramos tu interés para que Héroes Migrantes llegue a {selected ? selected[1] : "tu país"}. Si querés, podés seguir la conversación con Luciana por WhatsApp.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {waLink && (
                <button onClick={continuar} data-testid="modal-continuar-luciana" className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-extrabold text-white" style={{ background: BLUE }}>
                  <WhatsappLogo size={16} weight="fill" /> Hablar con Luciana
                </button>
              )}
              <button onClick={onClose} data-testid="modal-finish" className="text-sm font-extrabold" style={{ color: NAVY }}>Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
